<template>
  <v-row class="my-0">
    <v-col cols="6">
      <SummaryScoreCard
        v-if="showCompletedScoring"
        :summary="routineScoreSummary"
      />

      <ConfirmationModal
        modalTestID="checklistTab-modal-completeConfirmation"
        confirmTestID="checklistTab-button-confirmComplete"
        cancelTestID="checklistTab-button-cancelComplete"
        :show="showCompleteConfirmation"
        :isLoading="isSaving"
        :title="$t('routines.complete_warning_title')"
        :text="$t('routines.complete_warning_description')"
        :confirmText="$t('home.to_do.complete_action')"
        @cancel="closeCompleteConfirmation"
        @close="closeCompleteConfirmation"
        @confirm="confirmChecklistCompletion"
      />

      <v-sheet v-if="checklistDefinition" rounded outlined>
        <div
          class="d-flex mt-0 py-0 px-6 align-center justify-space-between"
          :style="{ flexWrap: 'wrap', minHeight: '66px' }"
        >
          <div class="d-flex align-center">
            <h3 class="font-weight-medium">
              {{ $t('checklist.checklist_one') }}
            </h3>

            <Button
              v-if="!isEditing && canUpdateRoutine"
              class="ml-2"
              color="primary"
              icon="mdi-pencil"
              size="small"
              plain
              :disabled="isLoading"
              :text="$t('checklist.edit_responses')"
              @click="isEditing = true"
            />
          </div>
          <div class="d-flex flex-column justify-center" v-if="hasSeparators">
            <v-switch
              v-model="isCollapsed"
              :label="$t('checklist.expand_all')"
            ></v-switch>
          </div>
        </div>
        <ChecklistReply
          canCancel
          ref="checklistReply"
          :canComplete="canUpdateRoutine && !routineIsCompleted"
          :canSave="canUpdateRoutine"
          :checklistDefinition="checklistDefinition"
          :checklistResponse="checklistResponse"
          :hasChecklistChanged="hasChecklistChanged"
          :isCollapsedAll="isCollapsed"
          :isEditing="isEditing"
          :isLoading="isSaving"
          :routine="routine"
          :routineDefinition="routineDefinition"
          :validateAnswers="routineIsCompleted"
          @checklistEditChange="$emit('checklistEditChange', $event)"
          @checklistHasChanged="$emit('checklistHasChanged', $event)"
          @getFindings="handleGetFindings"
          @isEditing="isEditing = $event"
          @save="handleSaveChecklist"
        />
      </v-sheet>
    </v-col>

    <v-col cols="6">
      <FindingsQuickView
        :routine="routine"
        :routineDefinition="routineDefinition"
        :findings="findings"
        :tableOptions="tableOptions"
        :isLoading="isLoading"
        @deleteClick="handleDeleteClick"
        @getFindings="handleGetFindings"
        @openAddressFindingDrawer="openAddressFindingDrawer"
        @openEditFindingDrawer="openEditFindingDrawer"
        @goToFindings="$emit('goToFindings')"
      />
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import { buildRoutineScoreSummary } from '@/components/Checklists/ChecklistReply/scoringDisplay'

import ChecklistReply from '@/components/Checklists/ChecklistReply/ChecklistReply.vue'
import ConfirmationModal from '@/components/Modals/ConfirmationModal.vue'
import FindingsQuickView from './FindingQuickView.vue'
import Button from '@/components/UI/Button'
import SummaryScoreCard from './SummaryScoreCard.vue'

export default {
  name: 'ChecklistTab',

  mixins: [mixin],

  props: {
    routine: {
      type: Object,
      required: true,
    },
    routineDefinition: {
      type: Object,
      required: true,
    },
    canUpdateRoutine: Boolean,
    isLoading: Boolean,
    findings: {
      type: Array,
      required: true,
    },
    tableOptions: {
      type: Object,
      required: true,
    },
    hasChecklistChanged: Boolean,
  },

  components: {
    ChecklistReply,
    ConfirmationModal,
    FindingsQuickView,
    Button,
    SummaryScoreCard,
  },

  data() {
    return {
      isCompleteConfirmationAccepted: false,
      isEditing: false,
      isCollapsed: false,
      pendingChecklistSaveArgs: null,
      isSaving: false,
      showCompleteConfirmation: false,
    }
  },

  mounted() {
    this.isEditing = !this.routineIsCompleted && this.canUpdateRoutine
  },

  computed: {
    ...mapGetters(['organizationId']),
    routineStatus() {
      return this.routine?.status ?? ''
    },

    checklistResponse() {
      return this.routine?.checklistResponse ?? null
    },
    checklistDefinition() {
      return this.routine?.checklist ?? null
    },
    routineIsCompleted() {
      return this.routineStatus === 'completed'
    },
    routineScoreSummary() {
      return buildRoutineScoreSummary(this.routine, this.checklistResponse)
    },
    showCompletedScoring() {
      return Boolean(this.routineIsCompleted && this.routineScoreSummary)
    },
    hasSeparators() {
      return this.checklistDefinition?.items?.some(
        item => item.type === 'separator'
      )
    },

    shouldWarnBeforeComplete() {
      return Boolean(this.routine?.checklist?.scoringType)
    },
  },

  methods: {
    openAddressFindingDrawer(finding) {
      this.$emit('openAddressFindingDrawer', finding)
    },

    openEditFindingDrawer(finding) {
      this.$emit('openEditFindingDrawer', finding)
    },

    handleDeleteClick(finding) {
      this.$emit('openDeleteFindingModal', finding)
    },

    handleGetFindings(options) {
      this.$emit('getFindings', options)
    },

    async handleSaveChecklist(args = {}) {
      const { checklist, updating, validated } = args

      if (
        validated &&
        this.shouldWarnBeforeComplete &&
        !this.isCompleteConfirmationAccepted
      ) {
        this.requestCompleteChecklistConfirmation(args)
        return
      }

      try {
        this.isSaving = true

        if (updating) {
          await this.updateChecklistResponse(checklist, validated)
        } else {
          await this.createChecklistResponse(checklist, validated)
        }

        this.isEditing = false
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isSaving = false
      }
    },

    requestCompleteChecklistConfirmation(args) {
      this.pendingChecklistSaveArgs = args
      this.showCompleteConfirmation = true
    },

    closeCompleteConfirmation() {
      this.pendingChecklistSaveArgs = null
      this.showCompleteConfirmation = false
    },

    attemptCompleteFromAlert() {
      this.isEditing = true
      this.$nextTick(() => {
        this.$refs.checklistReply?.saveResponses(true)
      })
    },

    async confirmChecklistCompletion() {
      const pendingArgs = this.pendingChecklistSaveArgs
      if (!pendingArgs) return

      this.showCompleteConfirmation = false
      this.pendingChecklistSaveArgs = null
      this.isCompleteConfirmationAccepted = true

      try {
        await this.handleSaveChecklist(pendingArgs)
      } finally {
        this.isCompleteConfirmationAccepted = false
      }
    },

    async createChecklistResponse(checklist, shouldCompleteRoutine) {
      const response = await api.checklist.createResponse(
        this.organizationId,
        checklist.id,
        {
          ...checklist,
          routine: this.routine.id,
          entityName: this.routine.name,
          entityId: this.routine.id,
          entityType: this.pascalCase(
            this.$constants.checklist.availableEntities.routine
          ),
          entitySite: this.routine.site,
        }
      )

      const checklistResponse = response.data

      if (shouldCompleteRoutine) {
        this.$emit('completeRoutine', {
          checklistResponse: checklistResponse.id,
          skipConfirmation: true,
          status: 'completed',
        })
        return
      }

      this.$emit('updateRoutine', {
        checklistResponse: checklistResponse.id,
        response: checklistResponse,
        status: null,
      })
    },

    async updateChecklistResponse(checklist, shouldCompleteRoutine) {
      await api.checklist.updateResponse(
        this.organizationId,
        checklist?.checklist,
        checklist?.id,
        {
          ...checklist,
          routine: this.routine.id,
          entityName: this.routine.name,
          entityId: this.routine.id,
          entityType: this.pascalCase(
            this.$constants.checklist.availableEntities.routine
          ),
          entitySite: this.routine.site,
        }
      )

      if (shouldCompleteRoutine) {
        this.$emit('completeRoutine', {
          skipConfirmation: true,
          status: 'completed',
        })
      }
    },
  },
}
</script>
